import {
  ReaderTaskType,
  ReaderTaskVisits,
} from '@dynamika-shared/types/reader';
import { ClinicalTrialInventoryScoringFormType } from '@dynamika-shared/types/clinicalTrialInventory';
import { done, start } from '../readerTaskOrchestration.slice';
import { DICOMViewerSlice } from '@restore/DICOMViewer/DICOMViewer.slice';
import { call, select } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import {
  fetchReaderSubTask,
  selectReaderSubTaskState,
} from '@restore/readerSubTask/readerSubTask.slice';
import { findAndTakeProperChildTask } from './findAndTakeProperChildTask';
import { detectIsViewerByUrl } from 'src/pages/Readings/ReadingTaskPage/hooks/useDetectIsViewerByUrl';
import { prepareCommonData } from './prepareCommonData';
import { ReaderSubTaskState } from '@restore/readerSubTask/readerSubTask.types';
import { goToRoute, runToast } from '@restore/tools';
import { prepareUrl } from 'src/utils/routerHelpers';
import { routesPath } from 'src/router/routes';
import { fetchReaderSubTaskWorker } from '@restore/readerSubTask/readerSubTask.saga';
import { UserRoles } from '@dynamika-shared/types/auth';
import {
  decideToStartSubTask,
  DecideToStartSubTaskResult,
} from './decideToStartSubTask';
import { dispatch } from '@dynamika-shared/utils/saga';
import { TASK_STATUSES } from './constants';
import { startReaderTask } from 'src/store-redux/readerTask/readerTask.slice';
import { startReaderTaskWorker } from 'src/store-redux/readerTask/readerTask.saga';
import {
  mapUserRoleToTaskEndpoint,
  mapUserRoleToViewerMode,
} from './viewerModeMapper';
import { selectAuthState } from '@restore/auth/auth.slice';
import { formManagementSlice } from '@restore/readerViewerSession/formManagement/formManagement.slice';
import { getRouteByRole } from 'src/utils/routeHelpers';

function* resetFormManagementIfNotDirty(): SagaIterator {
  const formManagementState = yield select(
    formManagementSlice.selectors.selectFormManagementState
  );

  if (!formManagementState.isDirty) {
    yield dispatch(formManagementSlice.actions.setFormDirty(false));
    yield dispatch(
      formManagementSlice.actions.changeScoringFormData({
        formData: null,
        isValid: false,
      })
    );
    yield dispatch(formManagementSlice.actions.setResetDirtyOnNextRender(true));
  }
}

function getCurrentRoleFromPath(): UserRoles {
  if (location.pathname.includes('reader')) {
    return UserRoles.READER;
  }

  if (location.pathname.includes('sponsor')) {
    return UserRoles.SPONSOR;
  }

  if (location.pathname.includes('ext-user')) {
    return UserRoles.EXTERNAL_USER;
  }

  return UserRoles.PROJECT_MANAGER;
}

export function* handleGroupTaskSaga(subTaskId?: string): SagaIterator {
  const {
    task,
    measurements,
    instruments,
    scoringForms,
    formConfig,
    labelGroups,
    clinicalTrialName,
  } = yield call(prepareCommonData);

  // Scoring forms configured for this trial (code + id). A patient (grouped)
  // form uses this to whitelist each visit's scoringCode and to resolve its own
  // scoringFormId, so it can host and self-scope a mix of versioned child forms
  // (e.g. LIVER_PDFF + LIVER_PDFF_V2). OHIF stays generic: it ships the data and
  // the form owns the per-version filtering.
  const childScoringForms = scoringForms as ClinicalTrialInventoryScoringFormType[];

  const authState = yield select(selectAuthState);
  const selectedRole = authState?.data?.selectedRole;

  const isViewer = detectIsViewerByUrl(
    task?.clinicalTrialId ?? '',
    task?.id ?? '',
    selectedRole
  );

  // start parent task — FIRST pass only (no subTaskId).
  //
  // handleGroupTaskSaga runs twice on a longitudinal open: pass 1 (subTaskId
  // undefined) starts the parent, then the orchestrator navigates to the first
  // visit and re-enters with a subTaskId (pass 2). The parent's status usually
  // stays READING_PENDING after the first start, so without the `!subTaskId`
  // guard pass 2 re-runs startReaderTaskWorker — a duplicate `PUT …/start` AND,
  // because startReaderTaskWorker does a post-start shouldRefetch, a duplicate
  // single-segment `GET /reader/tasks/{parentId}`. The parent only needs
  // starting once; per-visit starts are handled by decideToStartSubTask via the
  // sub-task worker.
  if (!subTaskId && task.status === TASK_STATUSES.READING_PENDING) {
    yield call(
      startReaderTaskWorker,
      startReaderTask({
        clinicalTrialId: task.clinicalTrialId ?? '',
        parentTaskId: task.parentTaskId,
        taskId: task.id ?? '',
      })
    );
  }

  const sortedSubTasks = [...task.childTasks].sort(
    (a, b) => a.visitOrder - b.visitOrder
  );

  const subTaskToLoad = findAndTakeProperChildTask(sortedSubTasks);

  if (subTaskToLoad && !subTaskId) {
    // Reset scoring form state to avoid dirty flag when switching subtasks, but only if not dirty already
    yield call(resetFormManagementIfNotDirty);

    const isDetached =
      typeof location !== 'undefined' &&
      location.pathname.includes('viewer-detached');

    const currentRole = getCurrentRoleFromPath();

    const targetRoute = getRouteByRole(
      currentRole,
      isDetached ? 'subTaskViewerDetached' : 'subTaskViewer'
    );

    yield goToRoute(
      prepareUrl(targetRoute, [
        task.clinicalTrialId ?? '',
        task.id ?? '',
        subTaskToLoad.id,
      ])
    );
    yield dispatch(
      start({
        clinicalTrialId: task.clinicalTrialId ?? '',
        taskId: task.id ?? '',
        subTaskId: subTaskToLoad.id,
      })
    );
    return;
  }

  const subTaskIdToLoad = subTaskId ?? subTaskToLoad?.id ?? '';

  // No real sub-task to load: bail out. Fetching with an empty subTaskId makes
  // fetchReaderSubTaskWorker call fetchParentReaderTask with taskId='', which
  // prepareUrl collapses to the single-segment `/reader/tasks/{parentId}` — the
  // exact request the orchestrator already issued for the parent (duplicate GET).
  if (!subTaskIdToLoad) {
    return;
  }

  const taskEndpoint = mapUserRoleToTaskEndpoint(selectedRole, !!subTaskIdToLoad);

  yield call(
    fetchReaderSubTaskWorker,
    fetchReaderSubTask({
      clinicalTrialId: task.clinicalTrialId ?? '',
      taskId: task.id,
      endpoint: taskEndpoint,
      subTaskId: subTaskIdToLoad,
    })
  );

  const subTaskState: ReaderSubTaskState = yield select(
    selectReaderSubTaskState
  );

  const subTask = subTaskState.data[subTaskIdToLoad];

  if (!subTask) {
    return;
  }

  const { ableToLoad, shouldStart }: DecideToStartSubTaskResult = yield call(
    decideToStartSubTask,
    subTask,
    task.childTasks,
    taskEndpoint,
    task.id
  );

  if (!ableToLoad) {
    yield call(
      runToast,
      'error',
      'You can not load this task until finish previous task'
    );

    const userRole: UserRoles | null = yield select(
      (state: { auth?: { data?: { selectedRole?: UserRoles } } }) =>
        state?.auth?.data?.selectedRole || null
    );

    const redirectRoute =
      userRole === UserRoles.EXTERNAL_USER
        ? routesPath.externalUserSubjectsClinicalTrial
        : routesPath.readerClinicalTrialReadings;

    yield goToRoute(prepareUrl(redirectRoute, [task.clinicalTrialId ?? '']));
    return;
  }

  // If decideToStartSubTask kicked a start+refetch for this PENDING sub-task,
  // the in-store record has been replaced with the post-start payload (lesions
  // copied from the previous visit). Re-read it so the form below renders
  // against the fresh snapshot rather than the pre-start one.
  let freshSubTaskState = subTaskState;
  let freshSubTask = subTask;
  if (shouldStart) {
    freshSubTaskState = yield select(selectReaderSubTaskState);
    freshSubTask = freshSubTaskState.data[subTaskIdToLoad] ?? subTask;
  }

  const subTasks = sortedSubTasks
    .map(sst => freshSubTaskState.data[sst.id])
    .filter(Boolean);

  if (isViewer) {
    // Before rendering the form for a new subtask, ensure the previous form cache is cleared, but only if not dirty already
    yield call(resetFormManagementIfNotDirty);

    const viewerMode = mapUserRoleToViewerMode(selectedRole);

    yield dispatch(
      DICOMViewerSlice.actions.runViewer({
        role: selectedRole,
        mode: viewerMode,
      })
    );
  }

  const isCompleted =
    task?.status === 'COMPLETED' || freshSubTask?.status === 'COMPLETED';
  yield dispatch(formManagementSlice.actions.setReadOnly(isCompleted));

  yield dispatch(
    done({
      formConfig,
      childScoringForms,
      measurementsConfig: measurements,
      instrumentsConfig: instruments,
      clinicalTrialId: task?.clinicalTrialId ?? '',
      taskId: task?.id ?? '',
      task,
      readOnly: freshSubTask?.status === 'COMPLETED',
      subTasks: subTasks as ReaderTaskType[],
      currentSubTask: freshSubTask,
      clinicalTrialName,
      readingCode: task?.readingCode ?? '',
      currentVisit: freshSubTask.visits?.[0] as ReaderTaskVisits,
      labelGroups,
    })
  );
}
